import React from 'react'
import styled, { css } from 'styled-components'

import { numberise, pixelise } from '@jarvis.oddle/core/tools/style'
import {
  horizontalNav,
  margin,
  marginSizes
} from '@jarvis.oddle/core/theme/default'
import { Portal } from '@jarvis.oddle/core/components/Portal'
import { Column } from '@jarvis.oddle/core/components/Grid'
import VisibilitySensor from '@jarvis.oddle/core/components/VisibilitySensor'

import breakpoint from 'src/utils/styledComponentsBreakpoint'
import VerticalNavigation from 'src/components/molecules/VerticalNavigation'
import HorizontalNavigation from 'src/components/molecules/HorizontalNavigation'
import MobileNavigation from 'src/components/molecules/MobileNavigation'
import NotificationBanner from 'src/components/molecules/NotificationBanner'
import OrderingFlowContext from 'src/containers/OrderingFlowContext'
import { ResponsiveContext } from 'src/decorators/Responsive'
import { responsiveSectionStyle } from 'src/components/core/ResponsiveSection'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite } from 'src/redux/meta'

const navigationStyle = css<{
  topOffset: number
}>`
  display: none;
  ${breakpoint('desktop')`
    display: block;
    position: sticky;
    top: ${(props) =>
      pixelise(
        props.topOffset - 1
      )}; /* CSS bugs - sometimes there is a 1px gap between Header and NavigationBar */
    width: 100%;
    z-index: 1;
  `}
`

const HorizontalNavigationContainer = styled.div<{
  topOffset: number
  stuck: boolean
}>`
  ${navigationStyle}
  ${responsiveSectionStyle}
  && {
    transition: all 150ms;
    ${(props) =>
      props.stuck
        ? `
      max-width: 100%;
      padding-left: 0;
      padding-right: 0;
    `
        : ''};
  }
`

const StyledVerticalNavigation = styled(VerticalNavigation)`
  ${navigationStyle}
`

const mobileNavigationHeight = 40

const StyledMobileNavigation = styled(MobileNavigation)<{
  top: NumberConstructor
  stuck: boolean
  enableReservationsOnShop: boolean
}>`
  position: sticky;
  top: ${(props) => pixelise(props.top)};
  width: 100%;
  z-index: 1;
  height: ${pixelise(mobileNavigationHeight)};
  ${(props) => (props.stuck ? 'visibility: visible;' : 'visibility: hidden;')};
  ${breakpoint('desktop')`
    display: none;
  `}
`

const Wrapper = styled.div<{
  hideCategoriesNavigation: boolean
}>`
  position: relative;
  ${(props) =>
    !props.hideCategoriesNavigation
      ? `
    margin-top: ${pixelise(-1 * mobileNavigationHeight)};
  `
      : ''}
  ${breakpoint('desktop')`
    margin-top: 0;
  `}
`

const Sensor = styled.div<{
  topOffset: number
}>`
  width: 1px;
  height: 1px;
  position: absolute;
  top: ${(props) => pixelise(props.topOffset * -1)};
  ${breakpoint('desktop')`
    top: ${(props) =>
      pixelise((props.topOffset + numberise(horizontalNav.height)) * -1)};
  `}
`

const appendHrefs = (categories) =>
  categories.map((cat) => ({ ...cat, href: `/#${cat.id}` }))

interface CategoryVisibilityEmitter {
  emit: (event: string) => void
  off: (event: string, callback: (...args: unknown[]) => void) => void
  on: (event: string, callback: (...args: unknown[]) => void) => void
  once: (event: string, callback: (...args: unknown[]) => void) => void
}

interface CategoriesNavigationWrapperProps {
  children: ({ selectedItemId: string }) => JSX.Element
  categories: Menu.MenuData[]
  categoryVisibilityEmitter: CategoryVisibilityEmitter
}

interface CategoriesNavigationProps {
  categoryVisibilityEmitter: CategoryVisibilityEmitter
  horizontalNavChannel: string
  onClickItem: (e: React.FormEvent<HTMLInputElement>, catId: string) => void
  showHorizontal?: boolean
  hideCategoriesNavigation: boolean
  productSectionSize: number
  syncScroll: boolean
  categories: Menu.MenuData[]
  children: JSX.Element[]
  showHorizontalNav: boolean
}

const CategoriesNavigationWrapper = ({
  categoryVisibilityEmitter,
  categories,
  children
}: CategoriesNavigationWrapperProps): JSX.Element => {
  const categoryVisibilitiesRef = React.useRef({})
  const [selectedItemId, setSelectedItemId] = React.useState<string>('')
  const reversedCategories: Menu.MenuData[] = React.useMemo(
    () => [...categories].reverse(),
    [categories]
  )

  React.useEffect(() => {
    const onVisibilityChange = ({ isVisible, category }) => {
      categoryVisibilitiesRef.current[category.id] = isVisible
      const firstVisibleCategory = reversedCategories.find(
        (cat) => categoryVisibilitiesRef.current[cat.id]
      )
      setSelectedItemId(firstVisibleCategory ? firstVisibleCategory.id : '')
    }

    categoryVisibilityEmitter.on('visibilityChange', onVisibilityChange)

    return () => {
      categoryVisibilityEmitter.off('visibilityChange', onVisibilityChange)
    }
  }, [categoryVisibilityEmitter, reversedCategories])

  return children({ selectedItemId })
}

const CategoriesNavigation = ({
  categoryVisibilityEmitter,
  horizontalNavChannel,
  onClickItem,
  hideCategoriesNavigation,
  showHorizontalNav,
  categories = [],
  productSectionSize,
  children,
  syncScroll
}: CategoriesNavigationProps): JSX.Element => {
  const metaState = useAppSelector(selectDetailMenuSite)

  const { desktopHeight, mobileHeight } = React.useContext(OrderingFlowContext)
  const { desktop } = React.useContext(ResponsiveContext)
  const [stuck, setStuck] = React.useState(null)
  const topOffset = showHorizontalNav
    ? desktopHeight
    : desktopHeight + numberise(margin[marginSizes.medium])
  const items = appendHrefs(categories)
  const categoriesNavSize = 12 - productSectionSize

  return (
    <React.Fragment>
      {desktop && (
        <React.Fragment>
          {!hideCategoriesNavigation &&
            (showHorizontalNav ? (
              <Portal channel={horizontalNavChannel}>
                <CategoriesNavigationWrapper
                  categories={categories}
                  categoryVisibilityEmitter={categoryVisibilityEmitter}
                >
                  {({ selectedItemId }) => (
                    <HorizontalNavigationContainer
                      topOffset={topOffset}
                      stuck={stuck}
                    >
                      <HorizontalNavigation
                        items={items}
                        selectedItemId={selectedItemId}
                        onClickItem={onClickItem}
                      />
                    </HorizontalNavigationContainer>
                  )}
                </CategoriesNavigationWrapper>
              </Portal>
            ) : (
              <Column sm={12} lg={categoriesNavSize}>
                <CategoriesNavigationWrapper
                  categories={categories}
                  categoryVisibilityEmitter={categoryVisibilityEmitter}
                >
                  {({ selectedItemId }) => (
                    <StyledVerticalNavigation
                      syncScroll={syncScroll}
                      items={items}
                      selectedItemId={selectedItemId}
                      onClickItem={onClickItem}
                      topOffset={topOffset}
                    />
                  )}
                </CategoriesNavigationWrapper>
              </Column>
            ))}
        </React.Fragment>
      )}
      {!hideCategoriesNavigation && (
        // Need to move MobileNavigation to horizontalNavChannel using Portal so it can scale full width of screen
        <Portal channel={horizontalNavChannel}>
          <CategoriesNavigationWrapper
            categories={categories}
            categoryVisibilityEmitter={categoryVisibilityEmitter}
          >
            {({ selectedItemId }) => (
              <StyledMobileNavigation
                top={pixelise(mobileHeight === 198 ? 220 : mobileHeight - 1)}
                expandDownwards
                stuck={stuck}
                items={items}
                selectedItemId={selectedItemId}
                onClickItem={onClickItem}
                enableReservationsOnShop={
                  metaState?.channelEnabled?.reserve
                }
              />
            )}
          </CategoriesNavigationWrapper>
        </Portal>
      )}
      <Column sm={12} lg={productSectionSize}>
        <Wrapper hideCategoriesNavigation={hideCategoriesNavigation}>
          {/* NOTE: rootMargin is set to bottom 1000px to prevent HorizontalNavigationContainer to expand at the bottom of the viewport when user scroll to Product section */}
          <VisibilitySensor
            data-testid='categoriesNavigation.sensor'
            rootMargin='0px 0px 1000px 0px'
            onChange={(visible) => setStuck(!visible)}
          >
            <Sensor topOffset={topOffset} />
          </VisibilitySensor>
          <NotificationBanner />
          {children}
        </Wrapper>
      </Column>
    </React.Fragment>
  )
}

export default CategoriesNavigation
